/**
 * Employee availability in the By Employee planner (#2041). The week view has no hour
 * axis, so each day cell gets a slim 06–22 strip, hatched where the employee is
 * unavailable; the day view hatches the timeline background itself. A blocked day shows a
 * lock in the cell corner. All of it is informational: drop and click work as before.
 */
import type { I18n } from '@lingui/core';
import { useLingui } from '@lingui/react';
import { format } from 'date-fns';
import { Lock } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { AvailabilityRange, BlockedDay } from '@/types/availability';
import { isoWeekday } from './combinationLayout';
import { unavailableSegments } from './availabilityLines';

interface WindowLike {
  start_time: string;
  end_time: string;
  kind: string;
}

export interface DayAvailability {
  windows: WindowLike[];
  blocked?: BlockedDay;
}

/** The windows that apply to one employee on one date, and the blocked day if any. */
export function availabilityForDay(
  range: AvailabilityRange | undefined,
  employeeId: string,
  date: Date
): DayAvailability {
  if (!range) return { windows: [] };
  const key = format(date, 'yyyy-MM-dd');
  const weekday = isoWeekday(date);
  const windows = range.rules
    .filter(
      (r) =>
        r.employee_id === employeeId &&
        r.weekday === weekday &&
        r.valid_from <= key &&
        (!r.valid_to || r.valid_to >= key)
    )
    .sort((a, b) => a.start_time.localeCompare(b.start_time));
  const blocked = range.blocked_days.find((b) => b.employee_id === employeeId && b.day === key);
  return { windows, blocked };
}

/** "Available 08:00–13:00" / "Not available 12:00–14:00", joined for the tooltip. */
export function availabilityTitle(windows: WindowLike[], i18n: I18n): string {
  const parts: string[] = [];
  const available = windows.filter((w) => w.kind === 'available');
  const unavailable = windows.filter((w) => w.kind === 'unavailable');
  const join = (ws: WindowLike[]) => ws.map((w) => `${w.start_time}–${w.end_time}`).join(', ');
  if (available.length > 0) {
    parts.push(i18n._({ id: 'shifts.availability.stripAvailable', message: 'Available {windows}', values: { windows: join(available) } }));
  }
  if (unavailable.length > 0) {
    parts.push(i18n._({ id: 'shifts.availability.stripUnavailable', message: 'Not available {windows}', values: { windows: join(unavailable) } }));
  }
  return parts.join(' · ');
}

const percent = (minutes: number, startHour: number, endHour: number) =>
  ((minutes - startHour * 60) / ((endHour - startHour) * 60)) * 100;

interface StripProps {
  windows: WindowLike[];
  startHour: number;
  endHour: number;
}

/** The slim strip under a week cell's shifts. Nothing when the day has no windows. */
export function AvailabilityStrip({ windows, startHour, endHour }: StripProps) {
  const { i18n } = useLingui();
  const segments = unavailableSegments(windows, { startHour, endHour });
  if (windows.length === 0) return null;
  const title = availabilityTitle(windows, i18n);
  return (
    <div
      className="relative mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-success-muted"
      role="img"
      aria-label={title}
      title={title}
      data-testid="availability-strip"
    >
      {segments.map((s) => (
        <div
          key={s.startMinutes}
          className="availability-hatch absolute inset-y-0"
          style={{
            left: `${percent(s.startMinutes, startHour, endHour)}%`,
            width: `${percent(s.endMinutes, startHour, endHour) - percent(s.startMinutes, startHour, endHour)}%`,
          }}
          data-testid="availability-strip-segment"
        />
      ))}
    </div>
  );
}

/** The hatched unavailable hours behind a day-view timeline. */
export function AvailabilityHatch({ windows, startHour, endHour }: StripProps) {
  const { i18n } = useLingui();
  if (windows.length === 0) return null;
  const title = availabilityTitle(windows, i18n);
  return (
    <div className="pointer-events-none absolute inset-0" aria-hidden="true">
      {unavailableSegments(windows, { startHour, endHour }).map((s) => (
        <div
          key={s.startMinutes}
          className="availability-hatch absolute inset-y-0"
          style={{
            left: `${percent(s.startMinutes, startHour, endHour)}%`,
            width: `${percent(s.endMinutes, startHour, endHour) - percent(s.startMinutes, startHour, endHour)}%`,
          }}
          title={title}
          data-testid="availability-hatch-segment"
        />
      ))}
    </div>
  );
}

/**
 * A blocked day: in a week cell an inline first line with the lock and the note; on a
 * day-view timeline a lock pill in the corner, the note as its label.
 */
export function BlockedDayLock({ blocked, inline = false }: { blocked: BlockedDay; inline?: boolean }) {
  const { i18n } = useLingui();
  const label = blocked.note
    ? i18n._({ id: 'shifts.availability.lockWithNote', message: 'Day blocked: {note}', values: { note: blocked.note } })
    : i18n._({ id: 'shifts.availability.lock', message: 'Day blocked' });
  if (inline) {
    return (
      <div
        className="flex min-w-0 items-center gap-1 text-xs text-muted-foreground"
        role="img"
        aria-label={label}
        title={label}
        data-testid="blocked-day-lock"
      >
        <Lock className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        <span className="truncate" aria-hidden="true">
          {blocked.note || i18n._({ id: 'shifts.availability.lock', message: 'Day blocked' })}
        </span>
      </div>
    );
  }
  return (
    <span
      className="pointer-events-auto absolute right-1 top-1 z-10 rounded-full bg-background/90 p-0.5 text-muted-foreground shadow-sm"
      role="img"
      aria-label={label}
      title={label}
      data-testid="blocked-day-lock"
    >
      <Lock className="h-3.5 w-3.5" aria-hidden="true" />
    </span>
  );
}

/** Legend line of the By Employee views. */
export function AvailabilityLegend({ className }: { className?: string }) {
  const { i18n } = useLingui();
  return (
    <ul
      className={cn('flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground', className)}
      aria-label={i18n._({ id: 'shifts.availability.legendLabel', message: 'Availability' })}
      data-testid="availability-legend"
    >
      <li className="inline-flex items-center gap-1.5">
        <span className="availability-hatch inline-block h-3 w-5 rounded-sm border" aria-hidden="true" />
        {i18n._({ id: 'shifts.availability.legendUnavailable', message: 'Not available (stated by the employee)' })}
      </li>
      <li className="inline-flex items-center gap-1.5">
        <Lock className="h-3.5 w-3.5" aria-hidden="true" />
        {i18n._({ id: 'shifts.availability.legendBlocked', message: 'Day blocked by the employee' })}
      </li>
    </ul>
  );
}
